import { gql, useMutation } from "@apollo/client";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { capture } from "../../analytics";

export const CREATE_EVENT = gql`
  mutation CreateEvent($event: CreateEventInput) {
    createEvent(event: $event)
  }
`;

export const UPDATE_EVENT = gql`
  mutation EditEvent($event: UpdateEventInput) {
    updateEvent(event: $event)
  }
`;

const REMOVE_EVENT = gql`
  mutation RemoveEvent($removeEventId: ID!) {
    removeEvent(id: $removeEventId)
  }
`;

export const GET_EVENTS = gql`
  query GetAvailableEvents {
    getAvailableEvents {
      _id
      # owner
      time
      title
      description
      template {
        templateId
        primaryColor
        accentColor
      }
      rsvps {
        status
        partySize
      }
    }
  }
`;

export const EVENT_BY_ID = gql`
  query GetEventById($getEventByIdId: ID!) {
    getEventById(id: $getEventByIdId) {
      title
      time
      owner
      location
      logs
      gifts {
        name
        link
        giver
        id
      }
      rsvps {
        id
        guestName
        status
        email
        partySize
        createdAt
      }
      messages {
        id
        author
        text
        isOrganizer
        isPrivate
        privateRecipient
        guestToken
        imageUrl
        createdAt
      }
      description
      template {
        templateId
        primaryColor
        accentColor
        imageUrl
        imagePublicId
      }
      thankYouMessage
      autoReminders
      remindersSent
      photos {
        id
        url
        publicId
        uploadedBy
        createdAt
      }
      _version
      _id
      memoryBookPublic
      aiCurationEnabled
      narrative {
        status
        language
        generatedAt
        model
        promptVersion
        contentSignature
        photoCaptions {
          photoId
          caption
          isHighlight
        }
        pullQuotes {
          messageId
          quote
        }
        chapters {
          id
          title
          narration
          photoIds
          messageIds
        }
      }
    }
  }
`;

export interface SerializedGift {
  name: string;
  link?: string;
  id?: string;
}

export interface SerializedEventForm extends Record<string, unknown> {
  gifts: SerializedGift[];
  template: Record<string, string | null>;
  // Known form fields declared alongside the open index signature (all values are
  // assignable to `unknown`, so the dynamic `event[name] = …` writes still compile).
  // These may be absent depending on which inputs are mounted, hence optional — but
  // declaring them restores typo/autocomplete checking on the fields the code reads.
  title?: string;
  _id?: string;
  _version?: number;
}

export function getEventFromFormElement(form: HTMLFormElement): SerializedEventForm {
  const allElements = Array.from(form.elements).filter((p: any) => p.name);
  const checkboxes = allElements.filter((p: any) => p.type === "checkbox") as HTMLInputElement[];
  const elements = allElements
    .filter((p: any) => p.type !== "checkbox")
    .map((p: any) => ({ name: p.name, value: p.value, id: p.id }));

  // `template` is a stable object referenced by `event.template`; mutating it in
  // place keeps the typed `event` object intact (no wholesale reassignment that
  // would erase the SerializedEventForm shape).
  const template: Record<string, string | null> = {};
  const giftMap: Record<string, SerializedGift> = {};
  const event: SerializedEventForm = { gifts: [], template };

  elements.forEach((p) => {
    const giftMatch = p.name.match(/^gifts\[(\d+)\]\[(\w+)\]$/);
    if (giftMatch) {
      const idx = giftMatch[1];
      const field = giftMatch[2];
      if (!giftMap[idx]) giftMap[idx] = { name: "" };
      if (field === "name") {
        giftMap[idx].name = p.value.trim();
        if (p.id) giftMap[idx].id = p.id;
      } else if (field === "link") {
        giftMap[idx].link = p.value.trim() || undefined;
      }
    } else if (p.name.startsWith("template.")) {
      const key = p.name.replace("template.", "");
      template[key] = p.value || null;
    } else {
      event[p.name] = p.name === "_version" ? parseInt(p.value) : p.value;
    }
  });
  checkboxes.forEach((cb) => {
    event[cb.name] = cb.checked;
  });
  event.gifts = Object.values(giftMap).filter((g) => g.name);
  if (!template.templateId) {
    template.templateId = "generic";
  }
  return event;
}

export function getDataFromForm(e: React.FormEvent<HTMLFormElement>) {
  return getEventFromFormElement(e.currentTarget);
}

function useNewEvent() {
  const [createEvent, { data, loading, error }] = useMutation(CREATE_EVENT);
  const navigate = useNavigate();
  function submitFunction(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const event = getDataFromForm(e);
    return createEvent({
      variables: {
        event,
      },
    }).then((result) => {
      capture("event_created");
      return result;
    });
  }

  useEffect(() => {
    if (data?.createEvent) {
      navigate(`/edit/${data.createEvent}`, { state: { justCreated: true } });
    }
  }, [navigate, data]);
  return {
    error,
    loading,
    submitFunction,
  };
}

export default useNewEvent;

// "invalid" = a save is being WITHHELD because a required field (title) is empty.
// It is a visible, always-in-the-header state so a block is never silent, even when
// the offending field's inline error is on a hidden tab.
export type SaveState = "idle" | "saving" | "saved" | "error" | "invalid";

// Detail for the "error" state so the UI can tell a transient failure (offer Retry)
// from a server rejection (retrying the same payload is futile — show why instead).
export interface SaveError {
  retryable: boolean;
  message?: string; // server-provided reason, shown for non-retryable failures
}

/**
 * Classify a failed autosave mutation so the UI can decide whether Retry is useful.
 * A server rejection (BAD_USER_INPUT / auth) can never succeed on an identical retry,
 * so it must not offer a futile Retry; a network error (or unknown code) is treated
 * as transient and stays retryable.
 */
export function classifyAutosaveError(err: unknown): "validation" | "auth" | "network" {
  const code = (err as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> })
    ?.graphQLErrors?.[0]?.extensions?.code;
  if (code === "UNAUTHENTICATED" || code === "FORBIDDEN") return "auth";
  if (code === "BAD_USER_INPUT") return "validation";
  return "network";
}

function autosaveErrorMessage(err: unknown): string | undefined {
  return (err as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message;
}

// Dirty-check key: the whole payload minus _version (not a user edit).
// getEventFromFormElement emits keys in a deterministic order, so a
// plain stringify is a stable comparison. Do NOT pass a key array as the
// JSON.stringify replacer — that form is an allowlist and would strip nested
// template/gift properties, silently breaking change detection.
function snapshotKey(payload: Record<string, unknown>): string {
  const rest = { ...payload };
  delete rest._version;
  return JSON.stringify(rest);
}

export function useEventAutosave(id: string, formId: string, serverVersion: number) {
  const [updateEvent] = useMutation(UPDATE_EVENT, {
    refetchQueries: [{ query: EVENT_BY_ID, variables: { getEventByIdId: id } }],
  });
  const [status, setStatus] = React.useState<SaveState>("idle");
  // Required-field validity surfaced to the form (currently just the title). The
  // form never submits, so `required` on the input is decorative — the hook is the
  // one place that reads the whole payload, so it owns the gate.
  const [titleMissing, setTitleMissing] = React.useState(false);
  // Detail for the "error" status: retryable (transient) vs a server rejection.
  const [saveError, setSaveError] = React.useState<SaveError | null>(null);

  const versionRef = React.useRef(serverVersion);
  const lastSavedRef = React.useRef<string | null>(null); // snapshotKey of last-saved form
  const savingRef = React.useRef(false); // a save is currently in flight
  const rerunRef = React.useRef(false); // an edit arrived mid-save → re-run after it settles

  // Keep the working version in sync with the server, but only ever ADVANCE it.
  // The client bumps versionRef optimistically on a confirmed write; a refetch
  // that resolves after a later bump must not roll it backward (which would make
  // the next save a spurious version conflict).
  React.useEffect(() => {
    versionRef.current = Math.max(versionRef.current, serverVersion);
  }, [serverVersion]);

  // Capture the server snapshot once the form exists (it mounts after the async
  // load). No dep array so it runs each render until captured. doSave also sets
  // the baseline lazily, covering the mount-time TemplatePicker onChange whose
  // child effect can fire before this parent effect.
  React.useEffect(() => {
    if (lastSavedRef.current !== null) return;
    const form = document.getElementById(formId) as HTMLFormElement | null;
    if (form) {
      lastSavedRef.current = snapshotKey(getEventFromFormElement(form));
    }
  });

  // Transient "saved" → "idle".
  React.useEffect(() => {
    if (status !== "saved") return;
    const t = setTimeout(() => setStatus("idle"), 2000);
    return () => clearTimeout(t);
  }, [status]);

  const doSave = React.useCallback(async () => {
    const form = document.getElementById(formId) as HTMLFormElement | null;
    if (!form) return;

    // Serialize: never run two saves at once. Concurrent saves share versionRef,
    // so a second overlapping save would send a stale _version and land as a
    // spurious conflict (possibly leaving the newer edit unpersisted). Mark a
    // re-run instead; the in-flight save re-runs from the latest form below.
    if (savingRef.current) {
      rerunRef.current = true;
      return;
    }

    const payload = getEventFromFormElement(form);
    const key = snapshotKey(payload);

    // Keep the visible title-required error in sync with the CURRENT title on every
    // observation, before any short-circuit below — otherwise reverting an emptied
    // title back to its saved value (a no-op save) would strand the error on screen.
    const titleValid = !!payload.title?.trim();
    setTitleMissing(!titleValid);

    // Lazy baseline: the first observation establishes the last-saved snapshot
    // without saving (guards the mount-time TemplatePicker onChange).
    if (lastSavedRef.current === null) {
      lastSavedRef.current = key;
      return;
    }
    if (key === lastSavedRef.current) {
      // Nothing changed (e.g. reverted to the saved value). Clear a stale error or
      // block so a Retry/revert that finds nothing to do doesn't leave a warning stuck.
      setStatus((s) => (s === "error" || s === "invalid" ? "idle" : s));
      setSaveError(null);
      return;
    }

    // Required-field gate: never persist an empty title. This BLOCKS the save, but
    // NOT silently: `titleMissing` renders an inline field error (Details tab) AND
    // the "invalid" status shows in the always-visible header badge, so a block is
    // visible even when the user is on another tab. This is the key difference from
    // the removed silent past-date guard that caused the data-loss bug.
    if (!titleValid) {
      setSaveError(null);
      setStatus("invalid");
      return;
    }

    savingRef.current = true;
    const event = { ...payload, _version: versionRef.current };
    setSaveError(null);
    setStatus("saving");
    let succeeded = false;
    try {
      const res = await updateEvent({ variables: { event } });
      const modified = res.data?.updateEvent as number | null;
      if (modified === 1) {
        versionRef.current += 1; // resolver wrote _version + 1
        lastSavedRef.current = key;
        setStatus("saved");
        succeeded = true;
      } else {
        // modifiedCount 0 = version conflict (filter matched nothing). Retryable:
        // the refetch resyncs the version so a Retry (or the next edit) succeeds.
        capture("event_autosave_failed", { eventId: id, reason: "version_conflict" });
        console.error("Event autosave conflict (stale version):", { eventId: id });
        setSaveError({ retryable: true });
        setStatus("error");
      }
    } catch (err) {
      // Classify so we don't offer a futile Retry on a server rejection (a
      // validation/auth error can't succeed on an identical retry) and so telemetry
      // is accurate. console.error is a durable trace independent of analytics
      // (capture() no-ops when the PostHog key is unset).
      const reason = classifyAutosaveError(err);
      capture("event_autosave_failed", { eventId: id, reason });
      console.error("Event autosave failed:", err);
      const retryable = reason === "network";
      setSaveError({ retryable, message: retryable ? undefined : autosaveErrorMessage(err) });
      setStatus("error");
    } finally {
      savingRef.current = false;
      // Always consume the re-run flag, but only chain a trailing save after a
      // SUCCESS. On a failure auto-re-running is unhelpful either way: after a
      // version conflict the local version is stale, and after a network throw the
      // write outcome is unknown — so a blind retry would just resend and log a
      // redundant event_autosave_failed. We defer to the visible "Retry", which
      // re-runs from the current form (including any mid-save edit) on demand.
      const shouldRerun = rerunRef.current;
      rerunRef.current = false;
      if (shouldRerun && succeeded) {
        void doSave(); // trailing save picks up the latest form + advanced version
      }
    }
  }, [formId, updateEvent, id]);

  const requestSave = React.useCallback(() => void doSave(), [doSave]);
  const retry = React.useCallback(() => void doSave(), [doSave]);

  // Delegated listeners for native inputs (text/date/checkbox/gifts). `focusout`
  // bubbles (unlike `blur`); `change` covers checkboxes and fires on commit for
  // text inputs. Both call doSave immediately — no debounce — so an edit starts
  // its save synchronously and can't be dropped by a following navigation; the
  // in-flight serialization coalesces bursts. NO dependency array so the
  // listeners re-attach when the form mounts after the async load.
  React.useEffect(() => {
    const form = document.getElementById(formId);
    if (!form) return;
    const handler = () => void doSave();
    form.addEventListener("change", handler);
    form.addEventListener("focusout", handler);
    return () => {
      form.removeEventListener("change", handler);
      form.removeEventListener("focusout", handler);
    };
  });

  return { status, requestSave, retry, titleMissing, saveError };
}

export function useRemoveEvent(id: string) {
  const [deleteEvent, { loading, error }] = useMutation(REMOVE_EVENT, {
    variables: { removeEventId: id },
    refetchQueries: [{ query: GET_EVENTS }],
  });

  function removeEvent(e: React.MouseEvent) {
    e.preventDefault();
    deleteEvent();
  }

  return {
    error,
    loading,
    removeEvent,
  };
}

export const DELETE_UPLOADED_ASSET = gql`
  mutation DeleteUploadedAsset($publicId: String!) {
    deleteUploadedAsset(publicId: $publicId)
  }
`;

/**
 * Bin a Cloudinary asset the app has decided not to keep.
 *
 * Deliberately best-effort in the UI: the caller has already moved on, and a
 * failed cleanup is not something the user could act on. It is *not* silent for
 * us — every failure leaves billable storage behind, so it goes to telemetry
 * rather than only to a console nobody is reading.
 *
 * Only call this when the asset is certainly unreferenced. If a save may have
 * landed, deleting strands a saved record pointing at a dead URL.
 */
export function useDiscardUploadedAsset() {
  const [deleteAsset] = useMutation(DELETE_UPLOADED_ASSET);

  return (publicId: string) => {
    deleteAsset({ variables: { publicId } }).catch((err) => {
      console.error("Discarding uploaded asset failed:", err);
      capture("uploaded_asset_orphaned", { publicId });
    });
  };
}

export const GENERATE_UPLOAD_SIGNATURE = gql`
  mutation GenerateUploadSignature {
    generateUploadSignature {
      signature
      timestamp
      cloudName
      apiKey
    }
  }
`;

export const POST_ORGANIZER_MESSAGE = gql`
  mutation PostOrganizerMessage($message: PostMessageInput!) {
    postOrganizerMessage(message: $message)
  }
`;

export function usePostOrganizerMessage(eventId: string) {
  const [postOrganizerMessage, { loading, error }] = useMutation(
    POST_ORGANIZER_MESSAGE,
    {
      refetchQueries: [
        { query: EVENT_BY_ID, variables: { getEventByIdId: eventId } },
      ],
    }
  );

  const post = (text: string, isPrivate?: boolean, privateRecipient?: string, privateRecipientToken?: string, imageUrl?: string) => {
    const message: any = { eventId, author: "Organizer", text };
    if (isPrivate) {
      message.isPrivate = true;
      if (privateRecipient) message.privateRecipient = privateRecipient;
      if (privateRecipientToken) message.privateRecipientToken = privateRecipientToken;
    }
    if (imageUrl) message.imageUrl = imageUrl;
    return postOrganizerMessage({ variables: { message } });
  };

  return { post, loading, error };
}

const SEND_EVENT_REMINDER = gql`
  mutation SendEventReminder($eventId: ID!) {
    sendEventReminder(eventId: $eventId)
  }
`;

export function useSendEventReminder(eventId: string) {
  const [sendReminder, { loading, error }] = useMutation(SEND_EVENT_REMINDER);

  const send = () => sendReminder({ variables: { eventId } });

  return { send, loading, error };
}

const SEND_INVITATION_EMAIL = gql`
  mutation SendInvitationEmail($input: SendInvitationEmailInput!) {
    sendInvitationEmail(input: $input)
  }
`;

export const IS_ADMIN = gql`
  query IsAdmin {
    isAdmin
  }
`;

export const GET_EVENT_ANALYTICS = gql`
  query GetEventAnalytics($id: ID!) {
    getEventAnalytics(id: $id) {
      viewCount
      rsvpCount
      attendingCount
      maybeCount
      attendingPeople
      maybePeople
      declinedCount
      giftCount
      giftClaimedCount
      messageCount
      photoCount
    }
  }
`;

export const GET_ADMIN_ANALYTICS = gql`
  query GetAdminAnalytics {
    getAdminAnalytics {
      totalEvents
      totalViews
      totalRsvps
      totalGifts
      totalGiftsClaimed
      totalMessages
      totalPhotos
    }
  }
`;

export const GET_AI_USAGE_STATS = gql`
  query GetAiUsageStats {
    getAiUsageStats {
      period
      totalGenerations
      activeOwners
      topOwners {
        owner
        count
      }
    }
  }
`;

export function useSendInvitationEmail(eventId: string) {
  const [sendInvitation, { loading, error }] = useMutation(SEND_INVITATION_EMAIL);

  const send = (guestName: string, email: string) =>
    sendInvitation({ variables: { input: { eventId, guestName, email } } });

  return { send, loading, error };
}
